import {
  MINESWEEPER_CELL_COUNT,
  MINESWEEPER_HEIGHT,
  MINESWEEPER_MINE_COUNT,
  MINESWEEPER_WIDTH,
  type MinesweeperPuzzle,
} from './types';

export type MinesweeperMark = 0 | 1 | 2; // covered, revealed, flagged

export function neighborsOf(
  index: number,
  width = MINESWEEPER_WIDTH,
  height = MINESWEEPER_HEIGHT,
): number[] {
  const row = Math.floor(index / width);
  const column = index % width;
  const neighbors: number[] = [];
  for (let rowOffset = -1; rowOffset <= 1; rowOffset += 1) {
    for (let columnOffset = -1; columnOffset <= 1; columnOffset += 1) {
      if (rowOffset === 0 && columnOffset === 0) continue;
      const nextRow = row + rowOffset;
      const nextColumn = column + columnOffset;
      if (nextRow < 0 || nextRow >= height || nextColumn < 0 || nextColumn >= width) continue;
      neighbors.push(nextRow * width + nextColumn);
    }
  }
  return neighbors;
}

export function buildMineValues(
  mines: ReadonlySet<number>,
  width = MINESWEEPER_WIDTH,
  height = MINESWEEPER_HEIGHT,
): number[] {
  return Array.from({ length: width * height }, (_, index) =>
    mines.has(index)
      ? -1
      : neighborsOf(index, width, height).filter((neighbor) => mines.has(neighbor)).length,
  );
}

export function floodIndices(
  values: readonly number[],
  startIndex: number,
  width = MINESWEEPER_WIDTH,
  height = MINESWEEPER_HEIGHT,
): Set<number> {
  if (values[startIndex] < 0) return new Set([startIndex]);
  const revealed = new Set<number>([startIndex]);
  if (values[startIndex] !== 0) return revealed;
  const queue = [startIndex];
  while (queue.length > 0) {
    const index = queue.shift()!;
    for (const neighbor of neighborsOf(index, width, height)) {
      if (values[neighbor] < 0 || revealed.has(neighbor)) continue;
      revealed.add(neighbor);
      if (values[neighbor] === 0) queue.push(neighbor);
    }
  }
  return revealed;
}

export function revealInto(
  marks: readonly MinesweeperMark[],
  puzzle: MinesweeperPuzzle,
  index: number,
): { marks: MinesweeperMark[]; hitMine: boolean } {
  if (marks[index] !== 0) return { marks: [...marks], hitMine: false };
  if (puzzle.values[index] === -1) {
    const next = [...marks] as MinesweeperMark[];
    next[index] = 1;
    return { marks: next, hitMine: true };
  }
  const next = [...marks] as MinesweeperMark[];
  for (const revealed of floodIndices(puzzle.values, index, puzzle.width, puzzle.height)) {
    if (next[revealed] !== 2) next[revealed] = 1;
  }
  return { marks: next, hitMine: false };
}

export function chordInto(
  marks: readonly MinesweeperMark[],
  puzzle: MinesweeperPuzzle,
  index: number,
): { marks: MinesweeperMark[]; hitMine: boolean; changed: boolean } {
  if (marks[index] !== 1 || puzzle.values[index] <= 0) {
    return { marks: [...marks], hitMine: false, changed: false };
  }
  const neighbors = neighborsOf(index, puzzle.width, puzzle.height);
  if (neighbors.filter((neighbor) => marks[neighbor] === 2).length !== puzzle.values[index]) {
    return { marks: [...marks], hitMine: false, changed: false };
  }
  let next = [...marks] as MinesweeperMark[];
  let hitMine = false;
  let changed = false;
  for (const neighbor of neighbors) {
    if (next[neighbor] !== 0) continue;
    const result = revealInto(next, puzzle, neighbor);
    next = result.marks;
    hitMine ||= result.hitMine;
    changed = true;
  }
  return { marks: next, hitMine, changed };
}

export function isMinesweeperWon(marks: readonly MinesweeperMark[], puzzle: MinesweeperPuzzle) {
  return marks.every((mark, index) => puzzle.values[index] === -1 || mark === 1);
}

export function correctFlagCount(
  marks: readonly MinesweeperMark[],
  puzzle: MinesweeperPuzzle,
): number {
  return Math.min(puzzle.mineCount, puzzle.mines.filter((index) => marks[index] === 2).length);
}

export function minesweeperFailureDelta(correctFlags: number): number {
  return Math.max(0, 60_000 - Math.min(MINESWEEPER_MINE_COUNT, Math.max(0, correctFlags)) * 2_000);
}

export function solveMinesweeperLogically(puzzle: MinesweeperPuzzle): {
  solved: boolean;
  revealed: Set<number>;
  flagged: Set<number>;
} {
  const revealed = floodIndices(puzzle.values, puzzle.startIndex, puzzle.width, puzzle.height);
  const flagged = new Set<number>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const index of [...revealed]) {
      const value = puzzle.values[index];
      if (value <= 0) continue;
      const neighbors = neighborsOf(index, puzzle.width, puzzle.height);
      const covered = neighbors.filter(
        (neighbor) => !revealed.has(neighbor) && !flagged.has(neighbor),
      );
      const adjacentFlags = neighbors.filter((neighbor) => flagged.has(neighbor)).length;
      if (covered.length > 0 && value - adjacentFlags === covered.length) {
        for (const neighbor of covered) {
          if (!flagged.has(neighbor)) {
            flagged.add(neighbor);
            changed = true;
          }
        }
      }
      if (covered.length > 0 && adjacentFlags === value) {
        for (const neighbor of covered) {
          for (const opened of floodIndices(puzzle.values, neighbor, puzzle.width, puzzle.height)) {
            if (!revealed.has(opened)) {
              revealed.add(opened);
              changed = true;
            }
          }
        }
      }
    }
  }
  const solved = Array.from({ length: MINESWEEPER_CELL_COUNT }, (_, index) => index).every(
    (index) => puzzle.values[index] === -1 || revealed.has(index),
  );
  return { solved, revealed, flagged };
}
